import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视剧分类", to: "/category" },
  { label: "资讯栏目", to: "/news" },
  { label: "专题策划", to: "/special" },
];

export default function SiteHeader() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const solid = !transparent;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-colors duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur-md border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="奸情影视内容站首页">
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-md text-lg font-black font-heading ${
                solid ? "bg-primary-900 text-background-50" : "bg-background-50/15 text-background-50 backdrop-blur-sm"
              }`}
            >
              奸
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg md:text-xl font-black tracking-wide ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                奸情<span className="text-accent-500">·</span>影视
              </span>
              <span
                className={`mt-1 font-label text-[10px] uppercase tracking-[0.28em] ${
                  solid ? "text-secondary-600" : "text-background-200"
                }`}
              >
                Film &amp; Story
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative rounded-md px-3 lg:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? solid
                        ? "text-primary-800"
                        : "text-background-50"
                      : solid
                        ? "text-foreground-600 hover:text-primary-700 hover:bg-background-100"
                        : "text-background-100/85 hover:text-background-50 hover:bg-background-50/10"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative flex items-center gap-1.5">
                    {item.label}
                    {isActive && <span className="h-1 w-1 rounded-full bg-accent-500" />}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              className={`hidden sm:flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                solid
                  ? "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  : "border-background-50/40 text-background-100 hover:border-background-50 hover:text-background-50"
              }`}
              aria-label="搜索影视剧与资讯"
            >
              <i className="ri-search-line text-base" aria-hidden="true" />
              <span className="hidden lg:inline">搜索剧集 / 资讯</span>
            </Link>
            <Link
              to="/category"
              className={`hidden lg:inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                solid
                  ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                  : "bg-background-50 text-primary-900 hover:bg-accent-100"
              }`}
            >
              开始探索
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`md:hidden flex h-10 w-10 items-center justify-center rounded-md border transition-colors ${
                solid
                  ? "border-background-300 text-foreground-900"
                  : "border-background-50/40 text-background-50"
              }`}
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-slide-down">
          <div className="px-4 py-4">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium ${
                      isActive ? "bg-primary-50 text-primary-800" : "text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" aria-hidden="true" />
                </NavLink>
              ))}
              <NavLink
                to="/search"
                className="flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium text-foreground-700"
              >
                搜索内容
                <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
              </NavLink>
            </div>
            <Link
              to="/category"
              className="mt-3 flex items-center justify-center gap-1.5 rounded-md bg-accent-500 px-4 py-3 text-sm font-semibold text-primary-950"
            >
              开始探索影视剧
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}